import { createStore, useAtom, useAtomValue } from 'jotai'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, ConfirmationDialog, ConfirmationDialogRef } from '../../rapid-ui'
import {
	campaignDetailsBrandingAtom,
	campaignDetailsGenerationErrorAtom,
	campaignDetailsLanguageAtom,
	campaignDetailsLocationAtom,
	campaignDetailsLogoUploadAtom,
	campaignDetailsRegenerateFromManualAtom,
	campaignDetailsRegenerateFromUrlAtom,
	campaignDetailsRequirementsAtom,
	campaignDetailsStatusAtom,
	campaignDetailsTitleAtom,
} from '../state'
import { BrandingArea } from './branding-area'
import { DetailsArea } from './details-area'
import { campaignDetailsValidationSchema, campaignUrlValidationSchema } from './validation'

export type EditModeDetailsAndBrandingProps = {
	brandStore?: ReturnType<typeof createStore>
	campaignId?: string
}

export const EditModeDetailsAndBranding = memo(
	function EditModeDetailsAndBranding({ brandStore, campaignId }: EditModeDetailsAndBrandingProps) {
		const { t } = useTranslation('common')
		const [mode, setMode] = useState<string>('manual')
		const [url, setUrl] = useState<string>('')
		const confirmationRef = useRef<ConfirmationDialogRef>(null)

		// Campaign details atoms
		const [title, setTitle] = useAtom(campaignDetailsTitleAtom)
		const [location, setLocation] = useAtom(campaignDetailsLocationAtom)
		const [language, setLanguage] = useAtom(campaignDetailsLanguageAtom)
		const [description, setDescription] = useAtom(campaignDetailsRequirementsAtom)

		const [branding, setBranding] = useAtom(campaignDetailsBrandingAtom)

		const campaignStatus = useAtomValue(campaignDetailsStatusAtom)
		const generationError = useAtomValue(campaignDetailsGenerationErrorAtom)

		const onRegenerateFromUrl = useAtomValue(campaignDetailsRegenerateFromUrlAtom)
		const onRegenerateFromManual = useAtomValue(campaignDetailsRegenerateFromManualAtom)
		const onLogoUpload = useAtomValue(campaignDetailsLogoUploadAtom)

		const handleRegenerate = useCallback(async () => {
			// Show confirmation dialog
			try {
				await confirmationRef.current?.confirm({
					title: t('editor.regenerateCampaignTitle'),
					message: t('editor.regenerateCampaignMessage'),
				})
			} catch {
				// User cancelled
				return
			}

			// User confirmed, proceed with regeneration
			if (!branding) { return }

			if (mode === 'manual') {
				onRegenerateFromManual?.({
					title: title ?? '',
					location: location ?? '',
					language: language ?? '',
					requirements: description ?? '',
				}, branding)
			} else {
				onRegenerateFromUrl?.(url, branding)
			}
		}, [
			t,
			mode,
			onRegenerateFromManual,
			onRegenerateFromUrl,
			title,
			location,
			language,
			description,
			url,
			branding,
		])

		// Validation
		const manualValidation = useMemo(() => {
			return campaignDetailsValidationSchema.safeParse({
				title,
				location,
				language,
				requirements: description,
			})
		}, [title, location, language, description])

		const urlValidation = useMemo(() => {
			return campaignUrlValidationSchema.safeParse({ url })
		}, [url])

		const titleErrors = useMemo(() => {
			if (!manualValidation.success) {
				return z.flattenError(manualValidation.error).fieldErrors.title || []
			}
			return []
		}, [manualValidation])

		const locationErrors = useMemo(() => {
			if (!manualValidation.success) {
				return z.flattenError(manualValidation.error).fieldErrors.location || []
			}
			return []
		}, [manualValidation])

		const languageErrors = useMemo(() => {
			if (!manualValidation.success) {
				return z.flattenError(manualValidation.error).fieldErrors.language || []
			}
			return []
		}, [manualValidation])

		const descriptionErrors = useMemo(() => {
			if (!manualValidation.success) {
				return z.flattenError(manualValidation.error).fieldErrors.requirements || []
			}
			return []
		}, [manualValidation])

		const urlErrors = useMemo(() => {
			if (!urlValidation.success) {
				return z.flattenError(urlValidation.error).fieldErrors.url || []
			}
			return []
		}, [urlValidation])

		const isRegenerateDisabled = mode === 'manual'
			? !manualValidation.success || !branding?.id
			: !urlValidation.success || !branding?.id

		if (campaignStatus !== 'ready') {
			return <div>{t('common.loading')}</div>
		}

		return (
			<div>
				<ConfirmationDialog ref={confirmationRef} />
				<BrandingArea
					branding={branding?.id ? branding : null}
					onBrandingChange={(b) => b && setBranding(b)}
					onLogoUpload={onLogoUpload}
					brandStore={brandStore}
					campaignId={campaignId}
				/>
				<DetailsArea
					isReady={campaignStatus === 'ready'}
					mode={mode}
					onModeChange={setMode}
					title={title ?? ''}
					onTitleChange={setTitle}
					location={location ?? ''}
					onLocationChange={setLocation}
					language={language ?? ''}
					onLanguageChange={setLanguage}
					description={description ?? ''}
					onDescriptionChange={setDescription}
					url={url}
					onUrlChange={setUrl}
					titleErrors={titleErrors}
					locationErrors={locationErrors}
					languageErrors={languageErrors}
					descriptionErrors={descriptionErrors}
					urlErrors={urlErrors}
					scrapeError={generationError ?? undefined}
				/>
				<div className="mt-6 mb-4 flex justify-center">
					<Button type="colorful" disabled={isRegenerateDisabled} onClick={handleRegenerate}>
						{t('editor.regenerate')}
					</Button>
				</div>
			</div>
		)
	},
)
